/* CodeVoyage client UI: keep attachments contained; image bytes are validated and durably confirmed before the agent receives the customer turn. */
import React, { useState, useRef, useEffect } from 'react';
import { api, API_URL } from '../api';
import PreviewFrame, { previewPageUrl, withoutOwnerKey } from './PreviewFrame';
import AgentResponse from './AgentResponse';
import BuildProgressTimeline from './BuildProgressTimeline';
import ChatNextSteps, { PUBLISH_REQUEST_TEXT, nativeOfferTitle, requestedNativePanel, requestedNativePlatforms } from './ChatNextSteps';
import IosAppPanel from './IosAppPanel';
import AndroidPackagePanel from './AndroidPackagePanel';
import TargetPicker from './TargetPicker';
import MyAppButton from './MyAppButton';
import WelcomeHero from '../welcome/WelcomeHero';
import { claimFreshChatSession, safeSession } from '../welcome/welcomeVisit';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { useLocale } from '../i18n/LocaleContext';
import { normalizeImageFile } from '../utils/imageNormalization';

function previewStorageKey(threadId) {
  return `codevoyage_preview_${threadId}`;
}

function activeTaskStorageKey(threadId) {
  return `codevoyage_active_task_${threadId}`;
}

const MAX_IMAGE_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const MAX_TEXT_ATTACHMENT_BYTES = 512 * 1024;
const MAX_TEXT_ATTACHMENT_CHARS = 12000;
const MAX_PROJECT_ZIP_BYTES = 50 * 1024 * 1024;
const MAX_CHAT_TASK_CHARS = 8000;
const ATTACHMENT_ENVELOPE_CHARS = 220;

function isSupportedTextAttachment(file) {
  return file.type.startsWith('text/') || ['application/json', 'application/xml', 'application/javascript'].includes(file.type) || /\.(?:txt|md|csv|json|xml|js|jsx|ts|tsx|py|java|go|rb|php|cs|html|css|scss|sql|sh|ya?ml)$/i.test(file.name);
}

function isProjectZipAttachment(file) {
  return ['application/zip', 'application/x-zip-compressed'].includes(file.type) || /\.zip$/i.test(file.name);
}

export function attachmentForMessage(attachment) {
  if (!attachment) return null;
  const { file, ...safeAttachment } = attachment;
  return safeAttachment;
}

export function createImageAttachment(file, url) {
  return { kind: 'image', name: file.name, url, type: file.type, size: file.size, file };
}

// An active image edit can outlive this browser tab. Keep only the already-confirmed evidence
// ids plus a display-safe filename next to the durable task id; never persist image bytes,
// object URLs, facts, or old thread-wide evidence into the next customer turn.
export function compactActiveTaskVisualEvidence(evidence = []) {
  if (!Array.isArray(evidence)) return [];
  return evidence
    .filter(item => item && typeof item.id === 'string' && /^vision_[A-Za-z0-9]{20,64}$/.test(item.id))
    .slice(0, 3)
    .map(item => ({ id: item.id, fileName: String(item.fileName || 'תמונה מצורפת').replace(/[\r\n]/g, ' ').slice(0, 160) }));
}

function readSavedActiveTask(threadId) {
  try {
    const saved = JSON.parse(localStorage.getItem(activeTaskStorageKey(threadId)) || 'null');
    return typeof saved?.taskId === 'string' && saved.taskId ? saved : null;
  } catch {
    return null;
  }
}

function readSavedPreview(threadId) {
  try {
    const saved = JSON.parse(localStorage.getItem(previewStorageKey(threadId)) || 'null');
    return typeof saved?.url === 'string' && saved.url ? saved : null;
  } catch {
    return null;
  }
}

// SpeechRecognition repeatedly revises interim hypotheses. Merge the original input with
// final and interim segments explicitly so a revised phrase replaces the prior draft.
export function composeVoiceInput(baseText, finalText, interimText) {
  return [baseText, finalText, interimText].map(value => String(value || '').trim()).filter(Boolean).join(' ');
}

// Real bug found live (2026-09-03): a brand-new customer's very first build (taskKind
// 'build', no existing project at all - e.g. a signed plan just approved via
// approveChatPlan) shows this generic fallback for the few seconds before the first real
// t.currentActivity/activityLog entry arrives from polling. The fallback unconditionally
// said "...לפרויקט הקיים" ("...for the EXISTING project") - flatly wrong and confusing
// for a customer who has never built anything yet (reproduced live: a brand-new tenant's
// first-ever chat build showed exactly this text immediately after plan approval).
// taskKind now disambiguates: 'build' (BUILD_NEW/Cloud Sandbox, no prior project) gets
// neutral "new work" wording; 'agent'/unset (MODIFY_REQUEST, an actual existing project)
// keeps the original, accurate "existing project" wording.
export function describeAgentProgress(buildActivity, liveProgress, taskKind) {
  if (buildActivity?.current) return buildActivity.current;
  const toolLabels = {
    list_files: 'ממפה את מבנה הפרויקט הקיים לפני שינוי.',
    read_file: 'קורא את הקוד הקיים כדי לשמור על מה שכבר עובד.',
    write_file: 'מעדכן את הקוד של הפרויקט בהתאם לבקשה.',
    run_command: 'מכין את הפרויקט לבדיקה בסביבת הבנייה.',
    web_search: 'בודק מידע נדרש לצורך המימוש.'
  };
  if (liveProgress?.tool) return toolLabels[liveProgress.tool] || 'מבצע שלב עבודה בפרויקט.';
  if (liveProgress?.iteration) return 'מכין את השלב הבא בבנייה ומוודא שלא נדרס קוד קיים.';
  return taskKind === 'build' ? 'מנתח את הבקשה ומכין תוכנית עבודה לפרויקט החדש.' : 'מנתח את הבקשה ומכין תוכנית עבודה לפרויקט הקיים.';
}

function progressMessageFromEvent(data) {
  if (data.type === 'iteration_start') return 'מכין את שלב העבודה הבא בפרויקט.';
  if (data.type === 'tool_start') return describeAgentProgress(null, { tool: data.tool, iteration: data.iteration });
  if (data.type === 'completion_gate_failed') return 'מאמת שהדרישה הושלמה בפועל לפני הצגת תוצאה.';
  if (data.type === 'cost_warning') return 'ממשיך בבדיקה מבוקרת כדי למנוע עבודה מיותרת.';
  return '';
}

// Rebuilds the step list from the server's own record of the task's progress events, so a
// realtime event missed by the socket (HTTP long-polling in production) still shows up.
export function progressEntriesFromEvents(events = []) {
  let entries = [];
  for (const event of Array.isArray(events) ? events : []) entries = appendProgressEntry(entries, progressMessageFromEvent(event || {}));
  return entries;
}

// A finished task's result is shown once: if the last agent message already has the same text
// (loaded from the saved chat history), it gains the Preview/details instead of being repeated.
export function mergeFinishedAgentMessage(messages, finished) {
  const list = Array.isArray(messages) ? messages : [];
  const lastAgentIndex = list.map(message => message?.role).lastIndexOf('agent');
  const last = lastAgentIndex >= 0 ? list[lastAgentIndex] : null;
  if (last && finished?.text && String(last.text || '').trim() === String(finished.text).trim()) {
    return list.map((message, index) => (index === lastAgentIndex ? { ...message, ...finished, animate: false } : message));
  }
  return [...list, finished];
}

function appendProgressEntry(entries, message, source = 'agent') {
  if (!message || entries[entries.length - 1]?.message === message) return entries;
  // Keep the whole build's activity (bounded); the timeline shows the last steps and counts all.
  return [...entries.slice(-199), { id: `${source}-${Date.now()}-${entries.length}`, message, at: new Date().toISOString(), source }];
}

// Real production crash found live (2026-09-02): a transcript entry persisted before earlier
// guards existed (agentApiCall.js/geminiAgentAdapter.js now always default a missing tool call's
// arguments to {}) could still have input === undefined. JSON.stringify(undefined) returns the
// literal value undefined, not a string, so undefined.slice(...) threw uncaught - and because
// this rendered inside every chat message, ANY older thread containing one such entry
// white-screened the whole tab the moment it was opened (confirmed live via the new "all
// conversations" list added in this same release, which made old threads reachable/re-rendered
// for the first time). Never crash on old or malformed stored data - render best-effort text.
export function formatTranscriptEntry(entry) {
  const tool = entry?.tool || 'tool';
  const input = JSON.stringify(entry?.input ?? {}).slice(0, 120);
  const outcome = entry?.result?.success ? 'ok' : `failed: ${entry?.result?.error || 'unknown error'}`;
  return `${tool}(${input}) → ${outcome}`;
}

// Same defensive rule as formatTranscriptEntry, applied to the separate "what was built" log
// (a different stored shape, populated by ProjectBuilder's live activity stream rather than the
// tool-call transcript above) - a malformed or legacy entry must render blank, never crash.
/**
 * Customer-facing build marker: "בנייה #N", the server time the build finished, and the full
 * technical reference kept for support. Falls back to a shortened reference only when the
 * server has no build number (older builds, non-Sandbox Previews).
 */
export const IMAGE_ONLY_SITE_TASK = 'הוסף את התמונה המצורפת לאתר במקום המתאים';

export function formatBuildMarker({ buildNumber, reference, timestamp } = {}) {
  const parsed = timestamp ? Date.parse(timestamp) : NaN;
  const timeLabel = Number.isFinite(parsed)
    ? new Date(parsed).toLocaleString('he-IL', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;
  const ref = reference ? String(reference) : '';
  const shortReference = ref.length > 22 ? `${ref.slice(0, 13)}…${ref.slice(-6)}` : ref;
  const number = Number.isInteger(buildNumber) && buildNumber > 0 ? buildNumber : null;
  const label = number ? `#${number}` : shortReference;
  const copyText = [
    ...(number ? [`מספר בנייה: ${number}`] : []),
    ...(ref ? [`מזהה בנייה: ${ref}`] : []),
    ...(timeLabel ? [`תאריך ושעה: ${timeLabel}`] : [])
  ].join('\n');
  return { label, timeLabel, isoTime: Number.isFinite(parsed) ? new Date(parsed).toISOString() : null, reference: ref, copyText };
}

export function formatBuildLogEntry(entry) {
  return entry?.message || '';
}

function AgentChat({ onCreditsChanged, socket, onNavigateToAccount, command = null, onCommandHandled = null, onNavigate = null, welcome = null, onWelcomeChange = null }) {
  const { t } = useLocale();
  const [initialThread] = useState(() => {
    // Persisted so returning to the app (page refresh, logging back in) reuses the same
    // conversation instead of randomly generating a new, empty one every time - which was
    // the actual root cause of a completed chat/build appearing to vanish on re-entry: the
    // server always kept the real history, but the client had no way to find it again once
    // the in-memory threadId that pointed to it was gone.
    // Personal welcome screen (2026-10-16): a new entry (new browser session or sign-in) opens a
    // fresh, empty chat with the greeting instead of the last conversation. The new id exists
    // only in this browser until the first message is sent - no empty chat is created on the
    // server. Later loads in the same session (refresh, screen switch) keep whatever chat is open.
    const existing = localStorage.getItem('nexus_chat_thread_id');
    // Always claim, even without a stored chat, so a refresh later in this session is not
    // mistaken for a new entry.
    const freshEntry = claimFreshChatSession();
    if (existing && !freshEntry) return { id: existing, fresh: false };
    const fresh = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('nexus_chat_thread_id', fresh);
    return { id: fresh, fresh: true };
  });
  const [threadId, setThreadId] = useState(initialThread.id);
  // Each chat owns its running build/edit (2026-10-18): a customer can open another chat while a
  // build runs, and start another build there. Polling of a task stops updating the screen once
  // its chat is no longer open; the task keeps running on the server and is resumed (from the
  // per-chat saved task) when the customer comes back to that chat.
  const threadRef = useRef(threadId);
  threadRef.current = threadId;
  const viewGenerationRef = useRef(0); // bumped on every chat switch: an old polling loop never resumes
  const viewKey = () => `${threadRef.current}#${viewGenerationRef.current}`;
  // A chat id created in this browser has no server history until its first message is sent, so
  // the greeting is shown at once instead of after the history/preview requests (2026-10-17: the
  // chat appeared first and the greeting only 6-7 s later on a cold start).
  const freshThreadRef = useRef(initialThread.fresh ? initialThread.id : null);
  const [messages, setMessages] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(initialThread.fresh);
  const [restoredVisualEvidence, setRestoredVisualEvidence] = useState([]);
  const [activeTaskVisualEvidence, setActiveTaskVisualEvidence] = useState([]);
  const imageObjectUrlsRef = useRef(new Set());

  const releaseImageObjectUrl = (url) => {
    if (typeof url !== 'string' || !imageObjectUrlsRef.current.has(url)) return;
    URL.revokeObjectURL(url);
    imageObjectUrlsRef.current.delete(url);
  };

  const releaseAllImageObjectUrls = () => {
    imageObjectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    imageObjectUrlsRef.current.clear();
  };

  useEffect(() => {
    if (threadId === freshThreadRef.current) { setHistoryLoaded(true); return undefined; }
    let cancelled = false;
    api.threadHistory(threadId).then(async res => {
      const rebuilt = [];
      for (const m of res.messages || []) {
        if (typeof m.content === 'string') {
          // The plain-string case is always the user's own original typed message - every
          // other message in this stored format is an internal agent-loop turn (tool_use /
          // tool_result blocks) that was never meant for direct display.
          rebuilt.push({
            role: 'user', text: m.content,
            attachmentName: m._attachmentContext?.name || null,
            attachmentKind: m._attachmentContext?.kind || null
          });
          } else if (m.role === 'assistant' && Array.isArray(m.content)) {
            const text = m.content.filter(b => b.type === 'text').map(b => b.text).join('\n').trim();
            // Legacy plan messages were persisted before per-action pricing moved out of the
            // customer workspace. Keep the stored audit record intact, but render the same
            // neutral approval copy as newly created plans.
            const displayText = m._pendingBuildPlan
              ? text.replace('עברו על השלבים, בדיקות האיכות ועלות הבנייה. התוכנית עצמה ללא חיוב; בנייה תתחיל רק בלחיצה מפורשת על אישור.', 'עברו על השלבים ובדיקות האיכות. הבנייה תתחיל רק לאחר אישור מפורש.')
              : text;
            if (displayText) rebuilt.push({
              role: 'agent',
              text: displayText,
              plan: m._pendingBuildPlan || null,
              research: m._conversationState?.research || null,
              attachmentName: m._zipUpload?.fileName || null,
              attachmentKind: m._zipUpload ? 'zip' : null
            });
          }
      }
      // Chat history is deliberately compact, so the latest preview URL lives separately.
      // Rehydrate it here to keep a finished website accessible after closing the preview,
      // switching tabs, or refreshing the client workspace.
      let savedPreview = readSavedPreview(threadId);
      // The server's project record is the source of truth across devices: a Preview built on
      // the phone must appear on the computer, and an older Preview remembered by this device
      // must never be shown instead of the project's current one. localStorage stays the
      // fallback when the server has no Preview for this thread or cannot be reached.
      try {
        const current = await api.threadPreview(threadId);
        if (current?.preview?.url) {
          savedPreview = {
            url: current.preview.url,
            sandboxJobId: current.preview.sandboxJobId || null,
            previewPages: current.preview.pages || [],
            previewExpiresAt: current.preview.expiresAt || null,
            target: current.preview.target || 'WEB',
            targetPlatforms: current.preview.targetPlatforms || [],
            projectId: current.preview.projectId || null,
            buildReference: current.preview.sandboxJobId || null,
            buildNumber: current.preview.buildNumber ?? null,
            buildTimestamp: current.preview.builtAt || null,
            savedAt: new Date().toISOString(),
            fromServer: true
          };
          localStorage.setItem(previewStorageKey(threadId), JSON.stringify(savedPreview));
        }
      } catch { /* keep the device copy */ }
      // A Preview token is intentionally short-lived. Never replay a saved token blindly
      // after returning to the client application: mint a fresh capability from the verified
      // sandbox artifact whenever its durable job id is known. If the network is temporarily
      // unavailable, retain the saved reference so the existing Refresh action can retry.
      if (savedPreview?.sandboxJobId && !savedPreview.fromServer) {
        try {
          const renewed = await api.createSandboxPreviewSession(savedPreview.sandboxJobId);
          savedPreview = {
            ...savedPreview,
            url: renewed.previewUrl,
            previewPages: renewed.previewPages || savedPreview.previewPages || [],
            previewExpiresAt: renewed.expiresAt || savedPreview.previewExpiresAt || null,
            buildNumber: renewed.buildNumber ?? savedPreview.buildNumber ?? null,
            buildTimestamp: renewed.builtAt || savedPreview.buildTimestamp || null,
            savedAt: new Date().toISOString()
          };
          localStorage.setItem(previewStorageKey(threadId), JSON.stringify(savedPreview));
        } catch {
          // The Viewer will keep the prior URL and offer a safe manual retry rather than
          // claiming a replacement Preview exists when the server cannot verify it.
        }
      }
      if (savedPreview && !rebuilt.some(message => message.previewUrl === savedPreview.url)) {
        rebuilt.push({ role: 'agent', text: 'התצוגה האחרונה של האתר שבנית נשמרה כאן.', previewUrl: savedPreview.url, sandboxJobId: savedPreview.sandboxJobId || null, previewPages: savedPreview.previewPages || [], previewExpiresAt: savedPreview.previewExpiresAt || null, target: savedPreview.target || 'WEB', targetPlatforms: savedPreview.targetPlatforms || [], projectId: savedPreview.projectId || null, buildReference: savedPreview.buildReference || savedPreview.sandboxJobId || null, buildNumber: savedPreview.buildNumber ?? null, buildTimestamp: savedPreview.buildTimestamp || null, restoredPreview: true });
      }
      const evidenceResponse = await api.confirmedImageEvidence(threadId).catch(() => ({ evidence: [] }));
      if (!cancelled) {
        const confirmedEvidence = Array.isArray(evidenceResponse.evidence) ? evidenceResponse.evidence : [];
        const savedActiveTask = readSavedActiveTask(threadId);
        const savedVisuals = compactActiveTaskVisualEvidence(savedActiveTask?.visualEvidence);
        const confirmedById = new Map(confirmedEvidence.map(evidence => [evidence.id, evidence]));
        const activeVisuals = savedVisuals.map(saved => ({
          ...saved,
          fileName: confirmedById.get(saved.id)?.fileName || saved.fileName
        }));
        if (activeVisuals.length > 0) {
          const lastUserIndex = rebuilt.map(message => message.role).lastIndexOf('user');
          if (lastUserIndex >= 0 && !rebuilt[lastUserIndex].attachmentName) {
            rebuilt[lastUserIndex] = {
              ...rebuilt[lastUserIndex],
              attachmentName: activeVisuals.map(evidence => evidence.fileName).join(', '),
              attachmentKind: 'image'
            };
          }
        }
        setActiveTaskVisualEvidence(activeVisuals);
        setRestoredVisualEvidence(confirmedEvidence);
        if (rebuilt.length > 0) setMessages(rebuilt);
      }
    }).catch(() => { /* no history yet for a brand-new thread, or a transient fetch failure - either way, starting with an empty chat is the correct fallback, not an error */ })
      .finally(() => { if (!cancelled) setHistoryLoaded(true); });
    return () => { cancelled = true; };
  }, [threadId]);
  const [input, setInput] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [attachmentNotice, setAttachmentNotice] = useState('');
  const [pendingVisualSend, setPendingVisualSend] = useState(null);
  const [listening, setListening] = useState(false);
  // Product type chosen by the customer for a NEW project in this chat (one mechanism for the
  // type button and the "more" menu). AUTO = detect from the request.
  const [targetChoice, setTargetChoice] = useState('AUTO');
  const [targetPickerOpen, setTargetPickerOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  useEffect(() => { setTargetChoice('AUTO'); setTargetPickerOpen(false); setMoreMenuOpen(false); }, [threadId]);
  const fileInputRef = useRef(null);
  const attachmentSelectionRef = useRef(0);
  const recognitionRef = useRef(null);
  const micStartingRef = useRef(false);
  const speechBaseRef = useRef('');
  const speechFinalRef = useRef('');
  const [sending, setSending] = useState(false);
  // The unsent draft survives a refresh (per conversation, this browser session only).
  const draftThreadRef = useRef(null);
  useEffect(() => {
    if (draftThreadRef.current !== threadId) return;
    const session = safeSession();
    if (input) session.set(`cv.chat.draft.${threadId}`, input); else session.remove(`cv.chat.draft.${threadId}`);
  }, [input, threadId]);
  useEffect(() => {
    draftThreadRef.current = threadId;
    const saved = safeSession().get(`cv.chat.draft.${threadId}`);
    if (saved) setInput(saved);
  }, [threadId]);
  const [error, setError] = useState('');
  const [needsUpgrade, setNeedsUpgrade] = useState(false);
  const handleApiError = (err) => {
    setError(err.response?.data?.error || err.message);
    setNeedsUpgrade(Boolean(err.response?.data?.insufficientCredits || err.response?.data?.killSwitchTripped));
  };
  const [checkpoints, setCheckpoints] = useState([]);
  const [restoring, setRestoring] = useState(false);
  // Real gap this closes (2026-08-22): previous chats were only ever reachable through the
  // Projects area (if they happened to have produced a usable build) - there was no way at
  // all, from inside the chat itself, to see or return to a chat the client had opened
  // earlier. GET /api/agent/threads already existed on the backend (added for a different
  // reason) but nothing in this UI ever called it.
  const [showChatList, setShowChatList] = useState(false);
  const [shareStatus, setShareStatus] = useState('');
  const [threadList, setThreadList] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [activeTaskId, setActiveTaskId] = useState(null);
  // Real fix, see describeAgentProgress above: which kind of task is running (a brand-new
  // build vs an edit of an existing project) so the generic "what's happening now" fallback
  // can say the right thing before real server-reported activity text arrives.
  const [activeTaskKind, setActiveTaskKind] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(true);
  const [expandedImage, setExpandedImage] = useState(null);
  const [liveProgress, setLiveProgress] = useState(null); // { iteration, tool, toolStatus }
  const [liveProgressLog, setLiveProgressLog] = useState([]);
  // Real bug this closes (found live, 2026-08-21): a build started from chat sets
  // task.currentActivity/task.activityLog server-side throughout the whole pipeline (the
  // same "Client Activity Stream" ProjectBuilder.jsx already shows on the Builder tab -
  // "Building and testing your app...", "Confirming everything you asked for is really
  // there...", etc: a plain-language narration of what's happening, specifically so the
  // customer isn't staring at a bare "Thinking..." for a multi-minute build). pollBuilderTask
  // below already fetches the full task on every poll, so t.currentActivity was arriving over
  // the wire the whole time - this component just never read it into state or rendered it,
  // unlike ProjectBuilder.jsx's pollTask (see its `if (task.currentActivity) setActivity(...)`).
  const [buildActivity, setBuildActivity] = useState(null); // { current, log: [{message, at}] }
  const bottomRef = useRef(null);
  const resumedTaskThreadRef = useRef(null);
  const [myAppOpenSignal, setMyAppOpenSignal] = useState(0);

  useEffect(() => {
    if (!expandedImage) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setExpandedImage(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [expandedImage]);

  useEffect(() => {
    if (!showChatList) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShowChatList(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [showChatList]);

  useEffect(() => {
    const viewport = window.visualViewport;
    const updateViewportHeight = () => {
      const height = viewport?.height || window.innerHeight;
      document.documentElement.style.setProperty('--chat-visual-viewport-height', `${Math.round(height)}px`);
    };
    updateViewportHeight();
    viewport?.addEventListener('resize', updateViewportHeight);
    viewport?.addEventListener('scroll', updateViewportHeight);
    window.addEventListener('resize', updateViewportHeight);
    return () => {
      viewport?.removeEventListener('resize', updateViewportHeight);
      viewport?.removeEventListener('scroll', updateViewportHeight);
      window.removeEventListener('resize', updateViewportHeight);
    };
  }, []);

  const rememberActiveTask = (taskId, kind, visualEvidence = []) => {
    const compactVisualEvidence = compactActiveTaskVisualEvidence(visualEvidence);
    localStorage.setItem(activeTaskStorageKey(threadId), JSON.stringify({ taskId, kind, visualEvidence: compactVisualEvidence, savedAt: new Date().toISOString() }));
    setActiveTaskVisualEvidence(compactVisualEvidence);
  };
  const forgetActiveTask = (id = threadId) => {
    localStorage.removeItem(activeTaskStorageKey(id));
    if (id === threadId) setActiveTaskVisualEvidence([]);
  };

  const rememberPreview = (url, sandboxJobId = null, previewPages = [], previewExpiresAt = null, target = 'WEB', targetPlatforms = [], buildReference = null, buildTimestamp = null, buildNumber = null) => {
    if (!url) return;
    const previous = readSavedPreview(threadId);
    const sameBuild = Boolean(sandboxJobId && previous?.sandboxJobId === sandboxJobId);
    localStorage.setItem(previewStorageKey(threadId), JSON.stringify({ url, sandboxJobId, previewPages, previewExpiresAt, target, targetPlatforms, buildReference: buildReference || (sameBuild ? previous.buildReference : null) || sandboxJobId || null, buildTimestamp: buildTimestamp || (sameBuild ? previous.buildTimestamp : null) || null, buildNumber: buildNumber ?? (sameBuild ? previous.buildNumber : null) ?? null, savedAt: new Date().toISOString() }));
    setPreviewVisible(true);
  };

  // Re-displays the last verified Preview saved for this thread (renewing its short-lived
  // session when possible), exactly as re-opening the chat does.
  const showLastVerifiedPreview = async () => {
    let saved = readSavedPreview(threadId);
    try {
      const current = await api.threadPreview(threadId);
      if (current?.preview?.url) {
        saved = { url: current.preview.url, sandboxJobId: current.preview.sandboxJobId || null, previewPages: current.preview.pages || [], previewExpiresAt: current.preview.expiresAt || null, target: current.preview.target || 'WEB', targetPlatforms: current.preview.targetPlatforms || [], projectId: current.preview.projectId || null, buildReference: current.preview.sandboxJobId || null, buildNumber: current.preview.buildNumber ?? null, buildTimestamp: current.preview.builtAt || null, fromServer: true };
        localStorage.setItem(previewStorageKey(threadId), JSON.stringify(saved));
      }
    } catch { /* fall back to this device's copy */ }
    if (!saved) return;
    if (saved.sandboxJobId && !saved.fromServer) {
      try {
        const renewed = await api.createSandboxPreviewSession(saved.sandboxJobId);
        saved = { ...saved, url: renewed.previewUrl || saved.url, previewPages: renewed.previewPages || saved.previewPages || [], previewExpiresAt: renewed.expiresAt || saved.previewExpiresAt || null, buildNumber: renewed.buildNumber ?? saved.buildNumber ?? null, buildTimestamp: renewed.builtAt || saved.buildTimestamp || null };
        localStorage.setItem(previewStorageKey(threadId), JSON.stringify(saved));
      } catch { /* keep the saved session; the Viewer offers its own refresh */ }
    }
    setMessages(prev => [...prev, { role: 'agent', text: 'זו התצוגה המאומתת האחרונה של האתר.', previewUrl: saved.url, sandboxJobId: saved.sandboxJobId || null, previewPages: saved.previewPages || [], previewExpiresAt: saved.previewExpiresAt || null, target: saved.target || 'WEB', targetPlatforms: saved.targetPlatforms || [], projectId: saved.projectId || null, buildReference: saved.buildReference || saved.sandboxJobId || null, buildNumber: saved.buildNumber ?? null, buildTimestamp: saved.buildTimestamp || null, restoredPreview: true }]);
    setPreviewVisible(true);
  };

  const shareApp = async () => {
    const saved = readSavedPreview(threadId);
    if (!saved?.url) { setError('אין עדיין תצוגה לשתף - בנו ואשרו תוכנית קודם.'); return; }
    let absoluteUrl;
    try { absoluteUrl = previewPageUrl(saved.url, 'index.html'); } catch { absoluteUrl = saved.url; }
    absoluteUrl = withoutOwnerKey(absoluteUrl); // shared with others: never the owner key
    if (!absoluteUrl) return;
    const shareData = { title: 'האתר שבניתי ב-CodeVoyage.ai', text: 'הצצה לאתר שבניתי - אפשר גם להוסיף אותו למסך הבית מהדפדפן.', url: absoluteUrl };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch (err) { if (err?.name === 'AbortError') return; }
    }
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      setShareStatus('copied');
      setTimeout(() => setShareStatus(''), 2000);
    } catch {
      setError(`לא ניתן להעתיק את הקישור אוטומטית. הקישור: ${absoluteUrl}`);
    }
  };

  const refreshPreview = async (previousUrl, sandboxJobId) => {
    if (!sandboxJobId) throw new Error('This preview cannot be renewed because no sandbox job is available.');
    const renewed = await api.createSandboxPreviewSession(sandboxJobId);
    const saved = readSavedPreview(threadId);
    rememberPreview(renewed.previewUrl, sandboxJobId, renewed.previewPages || [], renewed.expiresAt || null, saved?.target || 'WEB', saved?.targetPlatforms || []);
    setMessages(previous => previous.map(message => (
      message.previewUrl === previousUrl ? { ...message, previewUrl: renewed.previewUrl, sandboxJobId, previewPages: renewed.previewPages || message.previewPages || [], previewExpiresAt: renewed.expiresAt || null } : message
    )));
    return renewed;
  };

  useEffect(() => { setPreviewVisible(true); }, [threadId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Real-time status while the agent is actually working - which iteration it's on, and
  // which tool is currently running (or just finished). Filtered to the CURRENT task's id
  // so a stale event from a previous message in this same thread can never display here.
  useEffect(() => {
    if (!socket || !activeTaskId) return;
    const handleProgress = (data) => {
      if (data.taskId !== activeTaskId) return;
      if (data.type === 'iteration_start') setLiveProgress({ iteration: data.iteration, tool: null, toolStatus: null });
      else if (data.type === 'tool_start') setLiveProgress(p => ({ ...p, tool: data.tool, toolStatus: 'running' }));
      else if (data.type === 'tool_end') setLiveProgress(p => ({ ...p, tool: data.tool, toolStatus: data.success ? 'done' : 'failed' }));
      const message = progressMessageFromEvent(data);
      if (message) setLiveProgressLog(previous => appendProgressEntry(previous, message));
    };
    if (!socket) return undefined; // socket starts null and connects asynchronously in App.jsx - this effect correctly re-runs (via the [socket, ...] dependency below) once it's real, rather than crashing on a premature null
    socket.on('agent_task_progress', handleProgress);
    return () => socket.off('agent_task_progress', handleProgress);
  }, [socket, activeTaskId]);

  const refreshCheckpoints = () => {
    api.projectCheckpoints(threadId).then(res => setCheckpoints(res.checkpoints)).catch(() => {});
  };

  // Leaving a chat never stops its build: only this screen's view of it is cleared. The task keeps
  // running on the server and its id stays saved for that chat (resumed when the chat is reopened).
  const detachActiveTaskView = () => {
    viewGenerationRef.current += 1;
    resumedTaskThreadRef.current = null;
    setSending(false); setActiveTaskId(null); setActiveTaskKind(null);
    setLiveProgress(null); setLiveProgressLog([]); setBuildActivity(null); setActiveTaskVisualEvidence([]);
  };
  const newChat = () => {
    const fresh = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('nexus_chat_thread_id', fresh);
    freshThreadRef.current = fresh;
    detachActiveTaskView();
    releaseAllImageObjectUrls();
    setMessages([]); setCheckpoints([]); setAttachment(null); setPendingVisualSend(null); setActiveTaskVisualEvidence([]); setError(''); setThreadId(fresh);
  };
  const deleteChat = async () => {
    // Deleting a chat also deletes, on the server, every project that was built inside it
    // (source files removed from cloud storage too, not just the chat text) - see the
    // DELETE /api/agent/threads/:threadId handler. An already-deployed live site is not
    // affected; it stays live on its own hosting provider.
    if (!window.confirm('למחוק את השיחה לצמיתות? כל הפרויקטים שנבנו בשיחה זו יימחקו גם הם, כולל הקבצים בענן. אתר שכבר פורסם (Live) אצל ספק חיצוני יישאר פעיל שם.')) return;
    await api.clearThread(threadId);
    if (showChatList) setThreadList(list => list.filter(t => t.threadId !== threadId));
    newChat();
  };

  const openChatList = async () => {
    setShowChatList(true);
    setLoadingThreads(true);
    try {
      const res = await api.listThreads();
      setThreadList((res.threads || []).slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)));
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoadingThreads(false);
    }
  };

  const openThreadFromList = (id) => {
    if (id === threadId) { setShowChatList(false); return; }
    localStorage.setItem('nexus_chat_thread_id', id);
    detachActiveTaskView();
    releaseAllImageObjectUrls();
    setMessages([]);
    setCheckpoints([]);
    setActiveTaskVisualEvidence([]);
    setError('');
    setHistoryLoaded(false);
    setThreadId(id); // the existing [threadId] effect above loads its saved history
    setShowChatList(false);
  };

  const deleteThreadFromList = async (id) => {
    if (!window.confirm('למחוק שיחה זו לצמיתות? כל הפרויקטים שנבנו בשיחה זו יימחקו גם הם, כולל הקבצים בענן. אתר שכבר פורסם (Live) אצל ספק חיצוני יישאר פעיל שם.')) return;
    try {
      await api.clearThread(id);
      setThreadList(list => list.filter(t => t.threadId !== id));
      if (id === threadId) newChat();
    } catch (err) {
      handleApiError(err);
    }
  };

  // Chat actions from the side menu (App.jsx). Each command runs exactly once and is then
  // cleared, so returning to the chat later never repeats it. "My app" waits for the saved
  // history, then opens the "My app" panel of the latest app in this chat, or the projects
  // screen when this chat has none yet.
  useEffect(() => {
    if (!command) return;
    if (command.type === 'myApp' && !historyLoaded) return;
    onCommandHandled?.();
    if (command.type === 'new') newChat();
    else if (command.type === 'history') openChatList();
    else if (command.type === 'delete') deleteChat().catch(handleApiError);
    else if (command.type === 'share') shareApp();
    else if (command.type === 'myApp') {
      const latest = [...messages].reverse().find(m => m.previewUrl);
      if (latest?.projectId) setMyAppOpenSignal(Date.now());
      else onNavigate?.('projects');
    }
  }, [command, historyLoaded]);

  const handleRestore = async (checkpointId) => {
    setRestoring(true);
    setError('');
    try {
      await api.restoreCheckpoint(checkpointId);
      setMessages(prev => [...prev, { role: 'agent', text: 'Restored to the selected checkpoint. Changes made after that point are undone (still saved, in case you want to redo).' }]);
      refreshCheckpoints();
    } catch (err) {
      handleApiError(err);
    } finally {
      setRestoring(false);
    }
  };

  const handleSend = async (e, prepared = null) => {
    e?.preventDefault();
    if (listening) return;
    const activeAttachment = prepared?.attachment || attachment;
    const hasTypedInstruction = Boolean(prepared?.task || input.trim());
    // An image sent alone into a chat that already has a website is meant for that website:
    // it is placed in the site (the server materializes it on every non-read-only turn).
    // Without a website yet, or for other files, the image is explained instead.
    const threadHasWebsite = messages.some(message => message.previewUrl);
    const defaultAttachmentTask = activeAttachment?.kind === 'image' && threadHasWebsite ? IMAGE_ONLY_SITE_TASK : 'מה יש בקובץ המצורף?';
    const visibleTask = prepared?.task || input.trim() || (activeAttachment ? defaultAttachmentTask : '');
    if (visibleTask.length > MAX_CHAT_TASK_CHARS) return setError(`אורך ההודעה המרבי הוא ${MAX_CHAT_TASK_CHARS} תווים.`);
    const textAttachment = activeAttachment?.kind === 'text'
      ? { name: activeAttachment.name, type: activeAttachment.type, size: activeAttachment.size || activeAttachment.text.length, text: activeAttachment.text }
      : null;
    const task = visibleTask.trim();
    if (!task || (sending && !prepared)) return;
    // The chat this request belongs to, even if the customer opens another chat while it starts.
    const ownerView = prepared?.ownerView || viewKey();
    const ownerThreadId = threadId;
    const leftThisChat = () => viewKey() !== ownerView;
    const keepForOwnerChat = (taskId, kind) => {
      try { localStorage.setItem(activeTaskStorageKey(ownerThreadId), JSON.stringify({ taskId, kind, visualEvidence: [], savedAt: new Date().toISOString() })); } catch { /* storage unavailable */ }
    };

    // Render the exact customer turn immediately, but do not let the agent start from a local
    // browser preview. The image must first pass server validation, vision analysis, digest
    // matching and private asset storage; only the resulting confirmed evidence id is handed
    // to the existing agent/build path. This keeps image intake ordered without adding a new
    // build stage or touching Preview polling.
    if (activeAttachment?.kind === 'image' && !prepared?.visualEvidenceIds?.length) {
      if (!activeAttachment.file) return setError('לא ניתן לקרוא את קובץ התמונה. בחרו אותו שוב.');
      setMessages(prev => [...prev, { role: 'user', text: visibleTask, image: attachmentForMessage(activeAttachment), attachmentKind: 'image' }]);
      setInput('');
      setAttachment(null);
      setSending(true);
      setError('');
      try {
        const analyzed = await api.analyzeImageEvidence(activeAttachment.file, threadId);
        const confirmed = await api.confirmImageEvidence(analyzed.evidence.id, analyzed.evidence.facts, activeAttachment.file);
        setRestoredVisualEvidence(current => [...current.filter(evidence => evidence.id !== confirmed.evidence.id), confirmed.evidence].slice(-3));
        const preparedImageTask = {
          task: visibleTask,
          attachment: activeAttachment,
          visualEvidenceIds: [confirmed.evidence.id],
          visualEvidence: [confirmed.evidence],
          alreadyShown: true,
          ownerView
        };
        await handleSend(null, preparedImageTask);
      } catch (err) {
        handleApiError(err);
        setSending(false);
      }
      return;
    }

    if (!prepared?.alreadyShown) setMessages(prev => [...prev, { role: 'user', text: visibleTask, image: activeAttachment?.kind === 'image' ? attachmentForMessage(activeAttachment) : null, attachmentName: activeAttachment?.kind === 'text' || activeAttachment?.kind === 'zip' ? activeAttachment.name : null, attachmentKind: activeAttachment?.kind || null }]);
    setInput(''); setAttachment(null); setError(''); setSending(true);
    try {
      if (activeAttachment?.kind === 'zip') {
        setBuildActivity({ current: 'מקבל את ארכיון הפרויקט, בודק אותו ומחלץ אותו בסביבת עבודה מבודדת.', log: [] });
        // Priced ZIP upload (size tier set in Admin): price approval first, then a resumable upload
        // straight to Cloud Storage with progress. null = no ZIP fee for this size (one request).
        const mb = bytes => `${Math.round(bytes / 1048576)}MiB`;
        const priced = await api.uploadZipFromChat(activeAttachment.file, {
          threadId, instruction: hasTypedInstruction ? task : '',
          confirm: quote => Promise.resolve(window.confirm(`העלאת הקובץ "${quote.fileName}" (${mb(quote.size)}) עולה ${quote.credits} קרדיטים (${quote.label}). היתרה שלך: ${quote.balance} קרדיטים.\nבניית הפרויקט מהקובץ מחויבת בנפרד, כרגיל.\n\nלאשר ולהתחיל בהעלאה?`)),
          onProgress: (sent, total) => setBuildActivity({ current: `מעלה את הקובץ: ${Math.floor((sent / total) * 100)}% (${mb(sent)} מתוך ${mb(total)}). אם החיבור ייקטע, ההעלאה תמשיך מאותה נקודה.`, log: [] })
        });
        const started = priced || await api.uploadProject(activeAttachment.file, { threadId, instruction: hasTypedInstruction ? task : '' });
        if (leftThisChat()) { keepForOwnerChat(started.taskId, 'build'); return; }
        setMessages(prev => [...prev, {
          role: 'agent',
          text: hasTypedInstruction
            ? 'זיהיתי את ארכיון הפרויקט ואת ההוראה שצירפת. אני בודק את הפרויקט ומיישם את הבקשה באותו תהליך לפני התצוגה.'
            : 'זיהיתי ארכיון פרויקט. אני בודק את המבנה שלו בסביבה מבודדת; אפשר להמשיך באותה שיחה לאחר שהתצוגה תהיה מוכנה.',
          uploadTaskId: started.taskId,
          animate: true
        }]);
        setActiveTaskId(started.taskId); setActiveTaskKind('build'); rememberActiveTask(started.taskId, 'build'); setLiveProgress(null); setLiveProgressLog([]); pollBuilderTask(started.taskId); return;
      }
      // Attach image binaries to the exact customer turn that confirmed them. Previously
      // every later engineering message silently resent all images ever confirmed in the
      // thread, which becomes dangerous now that the server correctly materializes supplied
      // image references without guessing intent from wording. Historical evidence remains
      // visible in the conversation, but only a newly confirmed image is a build input.
      const visualEvidenceIds = prepared?.visualEvidenceIds || [];
      const visualEvidenceForTask = compactActiveTaskVisualEvidence(prepared?.visualEvidence || []);
      const started = await api.agentTask(task, threadId, visualEvidenceIds, textAttachment, targetChoice);
      if (leftThisChat()) {
        // The reply/task belongs to the chat the customer left; it runs and is shown there.
        if (!started.handled && started.taskId) keepForOwnerChat(started.taskId, started.taskKind === 'build' ? 'build' : 'agent');
        return;
      }
      if (started.handled) {
        let text = started.response; let previewUrl;
        const previewTarget = started.preview?.target || started.project?.target || 'WEB';
        const previewTargetPlatforms = started.preview?.targetPlatforms || started.project?.targetPlatforms || [];
        if (started.route?.intent === 'GET_PROJECT_STATUS') text = started.project ? `הפרויקט האחרון: ${started.project.name || started.project.id} — סטטוס: ${started.project.status || 'לא ידוע'}` : 'עדיין אין לך פרויקט פעיל.';
        else if (started.route?.intent === 'GET_PREVIEW_URL' || started.route?.intent === 'OPEN_PREVIEW') {
          if (started.preview?.url && started.preview?.verified) { text = 'הנה קישור התצוגה של הפרויקט:'; previewUrl = started.preview.url; }
          else text = 'אין עדיין קישור תצוגה מאומת לפרויקט הזה.';
        }
        const sandboxJobId = started.preview?.sandboxJobId || null;
        rememberPreview(previewUrl, sandboxJobId, started.preview?.pages || [], started.preview?.expiresAt || null, previewTarget, previewTargetPlatforms);
        setMessages(prev => [...prev, { role: 'agent', text: text || '(no response text)', plan: started.plan || null, research: started.discovery?.research || started.research || null, previewUrl, sandboxJobId, previewPages: started.preview?.pages || [], previewExpiresAt: started.preview?.expiresAt || null, target: previewTarget, targetPlatforms: previewTargetPlatforms, animate: true, download: started.download || null, adminUrl: previewUrl ? (started.preview?.adminUrl || null) : null }]);
        setSending(false); return;
      }
      const resolvedTaskKind = started.taskKind === 'build' ? 'build' : 'agent';
      setActiveTaskId(started.taskId); setActiveTaskKind(resolvedTaskKind); rememberActiveTask(started.taskId, resolvedTaskKind, visualEvidenceForTask); setLiveProgress(null); setLiveProgressLog([]); setBuildActivity(null);
      if (resolvedTaskKind === 'build') pollBuilderTask(started.taskId); else pollAgentTask(started.taskId);
    } catch (err) {
      if (leftThisChat()) return;
      handleApiError(err); setSending(false);
    }
  };

  const updatePendingVisualFact = (key, value, list = false) => {
    setPendingVisualSend(current => current ? { ...current, facts: { ...current.facts, [key]: list ? value.split(',').map(item => item.trim()).filter(Boolean) : value } } : current);
  };

  const confirmVisualAndSend = async () => {
    if (!pendingVisualSend || sending) return;
    setSending(true); setError('');
    try {
      const confirmed = await api.confirmImageEvidence(pendingVisualSend.evidence.id, pendingVisualSend.facts, pendingVisualSend.attachment.file);
      setRestoredVisualEvidence(current => [...current.filter(evidence => evidence.id !== confirmed.evidence.id), confirmed.evidence].slice(-3));
      const prepared = { task: pendingVisualSend.task, attachment: pendingVisualSend.attachment, visualEvidenceIds: [confirmed.evidence.id], visualEvidence: [confirmed.evidence], alreadyShown: true };
      setPendingVisualSend(null);
      await handleSend(null, prepared);
    } catch (err) {
      handleApiError(err); setSending(false);
    }
  };

  const approveChatPlan = async (plan) => {
    if (!plan?.approval?.token || sending) return;
    setSending(true);
    setError('');
    try {
      const started = await api.build({
        projectType: plan.approval.scope?.projectType || undefined,
        target: plan.target,
        targetPlatforms: plan.targetPlatforms || [],
        name: plan.projectName,
        features: plan.features || [],
        description: plan.intent,
        github: false,
        deploy: false,
        planApproval: plan.approval,
        threadId
      });
      setMessages(prev => [...prev, { role: 'agent', text: 'התוכנית אושרה והבנייה התחילה. אעדכן כאן בכל שלב עד לתוצאה המאומתת.', animate: true }]);
      setActiveTaskId(started.taskId);
      setActiveTaskKind('build');
      rememberActiveTask(started.taskId, 'build');
      setLiveProgress(null);
      setLiveProgressLog([]);
      setBuildActivity(null);
      pollBuilderTask(started.taskId);
    } catch (err) {
      handleApiError(err);
      setSending(false);
    }
  };

  // Shared by both the file-picker button and clipboard paste below, so an image reaches
  // the exact same vision-confirmation flow (analyzeImageEvidence -> confirm -> send)
  // regardless of how the customer attached it - there is only ever one path an image
  // takes into a message, which is also what the confirmed-evidence/completion-gate work
  // elsewhere in this project already assumes.
  const applyFile = async (file) => {
    if (!file) return;
    const selectionId = ++attachmentSelectionRef.current;
    const replacedAttachment = attachment;
    setError('');
    setAttachmentNotice(replacedAttachment ? `הקובץ „${replacedAttachment.name}” הוחלף ב־„${file.name}”.` : '');
    if (isProjectZipAttachment(file)) {
      if (file.size > MAX_PROJECT_ZIP_BYTES) return setError('גודל ארכיון ZIP המרבי הוא 50MB לאחר דחיסה.');
      setAttachment({ kind: 'zip', name: file.name, type: file.type || 'application/zip', size: file.size, file });
      return;
    }
    if (file.type.startsWith('image/')) {
      // Camera captures arrive as full-size sensor images; bound and straighten them once here
      // so the preview, the chat thumbnail, the vision check and the agent all get the same
      // image. Already-small gallery pictures come back unchanged (see imageNormalization.js).
      file = await normalizeImageFile(file);
      if (attachmentSelectionRef.current !== selectionId) return;
      if (file.size > MAX_IMAGE_ATTACHMENT_BYTES) return setError('גודל התמונה המרבי הוא 8MB.');
      const url = URL.createObjectURL(file);
      imageObjectUrlsRef.current.add(url);
      if (attachmentSelectionRef.current !== selectionId) {
        releaseImageObjectUrl(url);
        return;
      }
      if (attachment?.kind === 'image') releaseImageObjectUrl(attachment.url);
      setAttachment(createImageAttachment(file, url));
      return;
    }
    if (!isSupportedTextAttachment(file)) return setError('ניתן לצרף תמונות, קובצי טקסט או ארכיון ZIP של פרויקט בלבד.');
    if (file.size > MAX_TEXT_ATTACHMENT_BYTES) return setError('גודל קובץ הטקסט המרבי הוא 512KB.');
    try {
      const text = (await file.text()).slice(0, MAX_TEXT_ATTACHMENT_CHARS);
      if (!text.trim()) return setError('קובץ הטקסט שנבחר ריק.');
      if (attachmentSelectionRef.current === selectionId) setAttachment({ kind: 'text', name: file.name, type: file.type || 'text/plain', size: file.size, text, file });
    } catch {
      setError('לא ניתן היה לקרוא את הקובץ שנבחר.');
    }
  };

  const handleAttachment = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    applyFile(file);
  };

  // Real gap this closes (2026-09-02): the only way to attach an image was the file-picker
  // button - a screenshot or copied image on the clipboard (Ctrl+V) was silently ignored by
  // the plain-text textarea, the single most common way customers actually have a business
  // photo ready. This only intercepts an actual image on the clipboard; a normal text paste
  // is untouched and keeps working exactly as before.
  const handlePaste = (event) => {
    if (sending || pendingVisualSend) return;
    const items = Array.from(event.clipboardData?.items || []);
    const imageItem = items.find(item => item.kind === 'file' && item.type.startsWith('image/'));
    if (!imageItem) return;
    const file = imageItem.getAsFile();
    if (!file) return;
    event.preventDefault();
    const named = file.name ? file : new File([file], `pasted-image-${Date.now()}.${(file.type.split('/')[1] || 'png')}`, { type: file.type || 'image/png' });
    applyFile(named);
  };

  const toggleMic = () => {
    if (listening || micStartingRef.current) return recognitionRef.current?.stop();
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return setError('תמלול קולי אינו נתמך בדפדפן זה. נסו Chrome או Edge מעודכנים.');
    const recognition = new SpeechRecognition();
    speechBaseRef.current = input;
    speechFinalRef.current = '';
    micStartingRef.current = true;
    recognition.lang = 'he-IL'; recognition.interimResults = true; recognition.continuous = false;
    recognition.onstart = () => { micStartingRef.current = false; setListening(true); };
    recognition.onend = () => { micStartingRef.current = false; recognitionRef.current = null; setListening(false); };
    recognition.onerror = (event) => {
      micStartingRef.current = false;
      recognitionRef.current = null;
      setListening(false);
      if (!['aborted', 'no-speech'].includes(event.error)) setError('לא ניתן היה לתמלל את ההקלטה. בדקו את הרשאת המיקרופון.');
    };
    recognition.onresult = (event) => {
      let interimText = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const segment = event.results[index]?.[0]?.transcript || '';
        if (event.results[index].isFinal) speechFinalRef.current += `${speechFinalRef.current ? ' ' : ''}${segment.trim()}`;
        else interimText += `${interimText ? ' ' : ''}${segment.trim()}`;
      }
      setInput(composeVoiceInput(speechBaseRef.current, speechFinalRef.current, interimText));
    };
    recognitionRef.current = recognition;
    try { recognition.start(); } catch (error) { micStartingRef.current = false; recognitionRef.current = null; setError('לא ניתן היה להתחיל את ההכתבה. נסו שוב.'); }
  };

  useEffect(() => () => {
    recognitionRef.current?.stop();
    releaseAllImageObjectUrls();
  }, []);

  // Window sizing (2026-08-22): a chat edit that requires the Cloud Execution Sandbox
  // (see cloudSandboxTypes.js) can genuinely take up to the Cloud Run Job's own
  // timeoutSeconds: 900 (backend/execution-worker/cloud-run-job.yaml) - real npm install,
  // build, runtime start, and a Playwright browser-smoke pass, on a container that may also
  // need a cold start. The previous 60-attempt/1500ms window (90s total) was sized for the
  // OLD, synchronous, in-process agentLoop-only path and was never widened when Cloud
  // Sandbox verification was wired in - so on any real (non-trivial) sandboxed change, this
  // gave up and showed "taking longer than expected" long before the real, verified preview
  // was ready. Worse: because that previewUrl only ever arrives via this live poll response
  // (thread history has no previewUrl field - see the useEffect above), giving up here meant
  // the customer could never see it appear in chat at all, even after reloading the page -
  // only a follow-up "show me the preview" message would recover it (via GET_PREVIEW_URL,
  // which reads the persisted project record instead). ATTEMPTS/INTERVAL below give ~19
  // minutes of real wall-clock coverage, comfortably past the Job's 900s ceiling plus queue
  // dispatch and cold-start time, at a lower request rate than before.
  const pollAgentTask = (taskId, attemptsLeft = 380, ownerThread = viewKey()) => {
    fetchWithRetry(() => api.agentTaskStatus(taskId)).then(res => {
      if (viewKey() !== ownerThread) return; // this chat is no longer open
      const t = res.task;
      if (t.status === 'completed') {
        rememberPreview(t.previewUrl, t.sandboxJobId, t.previewPages || [], t.previewExpiresAt || null, 'WEB', [], t.sandboxJobId || t.id || taskId, t.builtAt || t.completedAt || t.updatedAt || null, t.buildNumber ?? null);
        // Returning to a chat whose edit finished meanwhile: the saved history already has the
        // summary, so the result is merged into it instead of shown twice.
        const finished = { role: 'agent', text: t.summary || 'הפעולה הסתיימה ללא סיכום טקסטואלי.', transcript: t.transcript, previewUrl: t.previewUrl, sandboxJobId: t.sandboxJobId, previewPages: t.previewPages || [], previewExpiresAt: t.previewExpiresAt || null, buildReference: t.sandboxJobId || t.id || taskId, buildNumber: t.buildNumber ?? null, buildTimestamp: t.builtAt || t.completedAt || t.updatedAt || null, projectId: t.sandboxProjectId || t.editProjectId || null, animate: true, storeAdminUrl: t.previewUrl ? (t.storeAdminUrl || null) : null };
        setMessages(prev => mergeFinishedAgentMessage(prev, finished));
        setSending(false);
        setActiveTaskId(null);
        setActiveTaskKind(null);
        forgetActiveTask();
        setLiveProgress(null);
        onCreditsChanged?.();
        refreshCheckpoints();
      } else if (t.status === 'failed') {
        // A change that ended without being applied keeps the last verified Preview. Show the
        // customer that plainly and put the previous Preview back in front of them right away
        // (previously it only reappeared after leaving and re-opening the chat).
        setMessages(prev => [...prev, { role: 'agent', text: t.previousPreviewKept ? t.error : `Failed: ${t.error}`, isError: true }]);
        setSending(false);
        setActiveTaskId(null);
        setActiveTaskKind(null);
        forgetActiveTask();
        setLiveProgress(null);
        if (t.previousPreviewKept) showLastVerifiedPreview();
      } else if (attemptsLeft > 0) {
        // Still running - the task keeps going server-side even if this tab is closed and
        // reopened later; polling just resumes watching it, it never restarts the work.
        if (Array.isArray(t.progress) && t.progress.length > 0) {
          const fromServer = progressEntriesFromEvents(t.progress);
          setLiveProgressLog(previous => (fromServer.length > previous.length ? fromServer : previous));
          const last = t.progress[t.progress.length - 1];
          if (last?.tool) setLiveProgress(previous => ({ ...(previous || {}), iteration: last.iteration || previous?.iteration || null, tool: last.tool, toolStatus: last.type === 'tool_end' ? (last.success ? 'done' : 'failed') : 'running' }));
        }
        setTimeout(() => pollAgentTask(taskId, attemptsLeft - 1, ownerThread), 3000);
      } else {
        setError('This is taking longer than expected — it may still finish in the background.');
        setSending(false);
      }
    }).catch(() => {
      if (viewKey() !== ownerThread) return;
      // A Cloud Run revision can restart or cold-start while the durable task continues.
      // Keep the existing task id and retry the polling loop instead of abandoning the
      // customer to a stale Preview or requiring a manual chat reopen after one outage.
      if (attemptsLeft > 0) {
        setError('החיבור התנתק לרגע — מתחברים מחדש וממשיכים לעקוב אחרי המשימה.');
        setTimeout(() => pollAgentTask(taskId, attemptsLeft - 1, ownerThread), 6000);
        return;
      }
      setError('לא ניתן היה להתחבר מחדש לבדיקת מצב המשימה. העבודה נשמרה בשרת ותתחדש כשתפתחו את הצ׳אט שוב.');
      setSending(false);
    });
  };

  // Mirrors ProjectBuilder.jsx's own pollTask (same api.task endpoint, same 40-attempt/1500ms
  // window, same field names) - a chat-started build is tracked exactly like a Builder-tab
  // build, because it IS one; this just renders the result as a chat message instead of the
  // Builder tab's separate result panel, reusing the same PreviewFrame the chat already
  // renders previewUrl through for agentLoop tasks (see the m.previewUrl block below).
  const pollBuilderTask = (taskId, attemptsLeft = 380, ownerThread = viewKey()) => {
    fetchWithRetry(() => api.task(taskId)).then(res => {
      if (viewKey() !== ownerThread) return; // this chat is no longer open
      const t = res.task;
      // See the buildActivity state declaration above - this is the fix. Read on every poll,
      // regardless of which branch below fires, so the customer sees the real narration
      // ("Building and testing your app...", "Found an issue - fixing it...", etc.) the whole
      // time the build is running, not just a generic "Thinking...".
      if (t.currentActivity || (Array.isArray(t.activityLog) && t.activityLog.length > 0)) {
        setBuildActivity({ current: t.currentActivity || null, log: t.activityLog || [] });
      }
      if (t.status === 'completed') {
        const target = t.config?.target || 'WEB';
        const targetPlatforms = t.config?.targetPlatforms || [];
        rememberPreview(t.previewUrl, t.sandboxJobId, t.previewPages || [], t.previewExpiresAt || null, target, targetPlatforms, t.sandboxJobId || t.id || taskId, t.builtAt || t.completedAt || t.updatedAt || null, t.buildNumber ?? null);
        setMessages(prev => [...prev, {
          role: 'agent',
          text: t.previewUrl ? 'הבנייה הושלמה ואומתה — אפשר לראות את התוצאה למטה.' : t.artifactUrl ? 'הבנייה הושלמה ואומתה בסביבה מבודדת: השרת עלה, ה-API ענה ובדיקת הדפדפן עברה. תצוגה חיה (Preview) לאפליקציות עם שרת ומסד נתונים עדיין לא זמינה במערכת; אפשר לבקש כאן "זיפ של קוד המקור" ולהפעיל את האפליקציה לפי ההוראות שבה.' : 'הבנייה הושלמה, אך עדיין אין תצוגה מאומתת להצגה.',
          previewUrl: t.previewUrl, sandboxJobId: t.sandboxJobId, previewPages: t.previewPages || [], previewExpiresAt: t.previewExpiresAt || null, target, targetPlatforms, projectId: t.projectId || t.id || null, buildReference: t.sandboxJobId || t.id || taskId, buildNumber: t.buildNumber ?? null, buildTimestamp: t.builtAt || t.completedAt || t.updatedAt || null, buildLog: t.activityLog, animate: true,
          storeAdminUrl: t.previewUrl ? (t.storeAdminUrl || null) : null
        }]);
        setSending(false); setActiveTaskId(null); setActiveTaskKind(null); forgetActiveTask(); setLiveProgress(null); setBuildActivity(null);
        onCreditsChanged?.();
      } else if (t.status === 'failed' && t.awaitingCustomerDecision && t.customerDecision) {
        // Same terminal state ProjectBuilder.jsx shows with an approve button - chat doesn't
        // have that control yet, but the customer still gets the same honest explanation
        // instead of a bare "failed", and can approve it from the Builder tab if they want to.
        setMessages(prev => [...prev, { role: 'agent', text: t.customerDecision.message, buildLog: t.activityLog, animate: true }]);
        setSending(false); setActiveTaskId(null); setActiveTaskKind(null); forgetActiveTask(); setLiveProgress(null); setBuildActivity(null);
      } else if (t.status === 'failed') {
        setMessages(prev => [...prev, { role: 'agent', text: `Failed: ${t.lastError || 'Build failed.'}`, buildLog: t.activityLog, isError: true }]);
        setSending(false); setActiveTaskId(null); setActiveTaskKind(null); forgetActiveTask(); setLiveProgress(null); setBuildActivity(null);
      } else if (attemptsLeft > 0) {
        setTimeout(() => pollBuilderTask(taskId, attemptsLeft - 1, ownerThread), 3000);
      } else {
        setError('This is taking longer than expected — it may still finish in the background.');
        setSending(false);
      }
    }).catch(() => {
      if (viewKey() !== ownerThread) return;
      // Do not turn a temporary backend restart into a failed customer build. The task id
      // remains in local storage and this loop keeps attempting recovery within its budget.
      if (attemptsLeft > 0) {
        setError('החיבור התנתק לרגע — מתחברים מחדש וממשיכים לעקוב אחרי הבנייה.');
        setTimeout(() => pollBuilderTask(taskId, attemptsLeft - 1, ownerThread), 6000);
        return;
      }
      setError('לא ניתן היה להתחבר מחדש לבדיקת מצב הבנייה. הבנייה נשמרה בשרת ותתחדש כשתפתחו את הצ׳אט שוב.');
      setSending(false);
    });
  };

  useEffect(() => {
    if (resumedTaskThreadRef.current === threadId) return;
    const saved = readSavedActiveTask(threadId);
    if (!saved) return;
    resumedTaskThreadRef.current = threadId;
    setActiveTaskId(saved.taskId);
    setActiveTaskKind(saved.kind || null);
    setActiveTaskVisualEvidence(compactActiveTaskVisualEvidence(saved.visualEvidence));
    setSending(true);
    const resume = setTimeout(() => {
      if (saved.kind === 'build') pollBuilderTask(saved.taskId);
      else pollAgentTask(saved.taskId);
    }, 0);
    return () => clearTimeout(resume);
  }, [threadId]);

  // The personal greeting shows while this conversation is empty; the first message (sent through
  // the normal chat flow below) replaces it with the conversation.
  const isWelcome = Boolean(welcome) && historyLoaded && messages.length === 0 && !sending;
  useEffect(() => { onWelcomeChange?.(isWelcome); }, [isWelcome]);
  useEffect(() => () => { onWelcomeChange?.(false); }, []);

  return (
    <>
      {shareStatus === 'copied' && <div className="chat-toast" role="status">✓ הקישור הועתק</div>}
      <div className={`agent-chat${isWelcome ? ' is-welcome' : ''}`}>

      {showChatList && (
        <div className="chat-list-layer">
          <button type="button" className="chat-list-backdrop" aria-label="סגור את רשימת השיחות" onClick={() => setShowChatList(false)} />
          <aside className="chat-list-drawer" aria-label="כל השיחות שלי">
            <div className="chat-list-header">
              <h3>כל השיחות שלי</h3>
              <button type="button" className="drawer-close" onClick={() => setShowChatList(false)} aria-label="סגור">×</button>
            </div>
            {loadingThreads && <span className="no-logs">טוען שיחות…</span>}
            {!loadingThreads && threadList.length === 0 && <span className="no-logs">אין עדיין שיחות שמורות.</span>}
            {!loadingThreads && threadList.length > 0 && (
              <ul className="chat-list-items">
                {threadList.map(t => (
                  <li key={t.threadId}>
                    <button
                      type="button"
                      className={`chat-list-item${t.threadId === threadId ? ' is-current' : ''}`}
                      onClick={() => openThreadFromList(t.threadId)}
                    >
                      <span className="chat-list-item-title">{t.title || 'שיחה ללא כותרת'}</span>
                      <span className="chat-list-item-meta">{t.threadId === threadId ? 'השיחה הנוכחית' : `${t.messageCount} הודעות`}</span>
                      <span className="chat-list-item-date">{t.updatedAt ? new Date(t.updatedAt).toLocaleString() : ''}</span>
                    </button>
                    <button type="button" className="chat-list-item-delete btn" aria-label="מחק שיחה" onClick={() => deleteThreadFromList(t.threadId)}>מחק</button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      )}

      {checkpoints.length > 0 && (
        <div className="checkpoint-panel" style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid var(--border)', borderRadius: '4px' }}>
          <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '0.5rem' }}>Undo a change</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {checkpoints.slice(0, 5).map(cp => (
              <div key={cp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12.5px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{cp.label} &mdash; {new Date(cp.createdAt).toLocaleString()}</span>
                <button type="button" className="btn" disabled={restoring} onClick={() => handleRestore(cp.id)}>
                  {restoring ? '...' : 'Restore this version'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="chat-window">
        {restoredVisualEvidence.length > 0 && (
          <section className="chat-restored-evidence" aria-label="פרטים מאושרים מתמונות בשיחה">
            <span className="chat-build-explanation-label">תמונות שאושרו בשיחה זו</span>
            <p>פרטי העסק שאושרו נשמרו להמשך השיחה. תמונת המקור נשמרת באופן פרטי רק עבור הבנייה שאושרה.</p>
            {restoredVisualEvidence.map(evidence => (
              <p key={evidence.id}><strong>{evidence.fileName}</strong>{evidence.facts?.businessName ? ` · ${evidence.facts.businessName}` : ''}{evidence.facts?.phoneNumbers?.length ? ` · ${evidence.facts.phoneNumbers.length} מספרי קשר` : ''}</p>
            ))}
          </section>
        )}
        {isWelcome && <WelcomeHero line={welcome.line} phrase={welcome.phrase} ready={welcome.ready} />}
        {!isWelcome && !welcome && messages.length === 0 && (
          <p className="no-logs">Try: "create a package.json for a small CLI tool" or "search for the current Node.js LTS version"</p>
        )}
        {(() => {
          // Only the most recent message with a previewUrl actually renders a live iframe -
          // showing one per historical message (there can be many across a long-lived
          // thread, especially now that history reloads on re-entry) would mean loading
          // several full app bundles simultaneously on every visit, which is both wasteful
          // and visually messy. Older completed tasks are still fully described in text.
          let lastPreviewIndex = -1;
          messages.forEach((m, i) => { if (m.previewUrl) lastPreviewIndex = i; });
          return messages.map((m, i) => (
            <div key={i} className={`chat-bubble ${m.role} ${m.isError ? 'error' : ''}`}>
              <div className="chat-bubble-label">{m.role === 'user' ? 'You' : 'Agent'}</div>
              {m.role === 'agent' && !m.isError ? <AgentResponse text={m.text} animate={Boolean(m.animate)} /> : <div className="chat-bubble-text">{m.text}</div>}
              {m.download?.url && (
                // A real link the customer taps: phones block downloads started by a script after a
                // network wait. The link itself is short-lived and carries its own random token.
                <a className="btn chat-download" href={`${API_URL}${m.download.url}`} download={m.download.fileName} target="_blank" rel="noopener noreferrer">
                  הורדת הזיפ ({m.download.fileName})
                </a>
              )}
              {m.role === 'agent' && m.previewUrl && (m.buildReference || m.sandboxJobId || m.buildNumber) && (() => {
                const marker = formatBuildMarker({ buildNumber: m.buildNumber, reference: m.buildReference || m.sandboxJobId, timestamp: m.buildTimestamp });
                return (
                  <div className="chat-build-reference" dir="rtl" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: '11px', opacity: 0.8, margin: '4px 0 6px', maxWidth: '100%' }}>
                    <span style={{ whiteSpace: 'nowrap' }}>בנייה:</span>
                    <span dir="ltr" title={marker.reference} style={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontWeight: 700 }}>{marker.label}</span>
                    {marker.timeLabel && <time dateTime={marker.isoTime} style={{ whiteSpace: 'nowrap' }}>{marker.timeLabel}</time>}
                    <button type="button" aria-label="העתק מספר בנייה ושעה" title="העתק פרטי בנייה" style={{ fontSize: '11px', padding: '2px 5px', flexShrink: 0 }} onClick={() => {
                      if (!navigator.clipboard?.writeText) { setError('העתקה אינה זמינה בדפדפן זה.'); return; }
                      navigator.clipboard.writeText(marker.copyText).catch(() => setError('לא ניתן להעתיק אוטומטית. ניתן לסמן ולהעתיק את מספר הבנייה.'));
                    }}>העתק</button>
                  </div>
                );
              })()}
              {m.research?.sources?.length > 0 && (
                <details className="chat-build-explanation" aria-label="מקורות מחקר">
                  <summary className="chat-build-explanation-label">מקורות שנבדקו</summary>
                  {m.research.sources.map((source, sourceIndex) => (
                    <p key={`${source.url}-${sourceIndex}`}>
                      <a href={source.url} target="_blank" rel="noopener noreferrer">{source.title || source.url}</a>
                      {source.retrievedAt ? ` · נבדק: ${source.retrievedAt}` : ''}
                    </p>
                  ))}
                </details>
              )}
              {m.plan && (
                <section className="chat-build-explanation" aria-label="תוכנית בנייה ממתינה לאישור">
                  <span className="chat-build-explanation-label">תוכנית בנייה — ללא חיוב עד אישור</span>
                  <p><strong>{m.plan.projectName}</strong> · יעד: {m.plan.target}</p>
                  {(m.plan.steps || []).map((step, stepIndex) => <p key={`${step.stage}-${stepIndex}`}>{stepIndex + 1}. <strong>{step.stage}</strong> — {step.detail}</p>)}
                  <p>הבנייה תתחיל רק לאחר אישור מפורש. יתרת השימוש זמינה באזור החשבון. לא יבוצעו פרסום או חיבור חיצוני בלי בחירה נפרדת.</p>
                  <button type="button" className="submit-btn" onClick={() => approveChatPlan(m.plan)} disabled={sending}>אישור תוכנית והתחלת Build</button>
                </section>
              )}
              {m.image?.url && (
                <figure className="chat-image" aria-label={t('chat.imageAttachment')}>
                  <button type="button" className="chat-image-thumbnail" onClick={() => setExpandedImage(m.image)} aria-label={t('chat.expandImage')}>
                    <img src={m.image.url} alt={m.image.name || t('chat.imageAttachment')} />
                    <span>{t('chat.expandImage')}</span>
                  </button>
                  <figcaption>
                    <span>{m.image.name || t('chat.imageAttachment')}</span>
                    <a href={m.image.url} download={m.image.name || 'codevoyage-image'}>{t('chat.downloadImage')}</a>
                  </figcaption>
                </figure>
              )}
              {m.attachmentName && <div className="chat-file-note">קובץ מצורף: {m.attachmentName}</div>}
              {m.transcript && m.transcript.length > 0 && (
                <details className="chat-transcript">
                  <summary>{m.transcript.length} tool call{m.transcript.length === 1 ? '' : 's'}</summary>
                  {m.transcript.map((entry, j) => (
                    <div key={j} className="chat-transcript-item">
                      {formatTranscriptEntry(entry)}
                    </div>
                  ))}
                </details>
              )}
              {/* Same "Client Activity Stream" ProjectBuilder.jsx shows live while building - a
                  plain-language record of what the agent actually did, kept after the build
                  finishes so the customer can see the explanation, not just the end result. */}
              {m.buildLog && m.buildLog.length > 0 && (
                <details className="chat-transcript">
                  <summary>מה נבנה &mdash; {m.buildLog.length} שלבים</summary>
                  {/* Same defensive rule as formatTranscriptEntry above, applied here too: a build
                      log entry is stored data that can outlive the code that wrote it, so a
                      malformed/legacy entry (entry === null, or missing .message) must render
                      blank rather than crash the whole chat - {entry.message} directly would
                      throw "Cannot read properties of null/undefined" exactly like the bug fixed
                      in formatTranscriptEntry. */}
                  {m.buildLog.map((entry, j) => (
                    <div key={j} className="chat-transcript-item">{formatBuildLogEntry(entry)}</div>
                  ))}
                </details>
              )}
              {m.previewUrl && m.buildLog && m.buildLog.length > 0 && (
                <section className="chat-build-explanation" aria-label="סיכום בנייה מאומת">
                  <span className="chat-build-explanation-label">מה נבנה ואומת</span>
                  <p>{m.buildLog[m.buildLog.length - 1]?.message || 'הבנייה הושלמה ונוצרה תצוגה מאומתת.'}</p>
                </section>
              )}
              {/* Real gap found live (2026-09-03): when a build produces more than one verified
                  entry page (today: a customer store plus a separate admin.html management
                  panel - see previewService.js's PREVIEW_PAGE_DEFINITIONS), the only way to
                  reach the second page was to open the full-screen preview overlay and notice
                  the tab switcher inside it (PreviewFrame's preview-page-tabs). The agent's own
                  chat reply can't include a real link at all - it finishes writing files before
                  previewService ever assigns a URL - so a customer who asked for "a separate
                  admin link" got a filename and an in-page button to look for, never an actual
                  link. This surfaces every verified page as its own real, directly-copyable URL
                  right in the chat reply, using the exact same previewPageUrl() the preview
                  overlay's own "copy link" button already relies on - purely additive, changes
                  nothing about how the single-page case or the preview overlay itself render. */}
              {m.previewUrl && Array.isArray(m.previewPages) && m.previewPages.length > 1 && (
                <section className="chat-build-explanation" aria-label="קישורים נפרדים לכל עמוד">
                  <span className="chat-build-explanation-label">שני קישורים נפרדים</span>
                  {m.previewPages.map(page => {
                    const pageUrl = previewPageUrl(m.previewUrl, page.path);
                    if (!pageUrl) return null;
                    const label = page.kind === 'admin' ? 'ניהול החנות' : 'החנות';
                    return (
                      <p key={page.id || page.path}>
                        {label}: <a href={pageUrl} target="_blank" rel="noopener noreferrer">{pageUrl}</a>
                      </p>
                    );
                  })}
                </section>
              )}

              {/* A store built from a ready template: the store link and its own management screen
                  (products, images from the gallery, orders) as two separate links. */}
              {m.previewUrl && m.storeAdminUrl && (
                <section className="chat-build-explanation" aria-label="קישור לחנות וקישור לניהול">
                  <span className="chat-build-explanation-label">שני קישורים נפרדים</span>
                  <p>החנות: <a href={previewPageUrl(withoutOwnerKey(m.previewUrl), 'index.html')} target="_blank" rel="noopener noreferrer">פתיחת החנות</a></p>
                  <p>ניהול החנות: <a href={previewPageUrl(m.storeAdminUrl, 'index.html')} target="_blank" rel="noopener noreferrer">פתיחת ניהול החנות</a> - מוצרים ומחירים, העלאת תמונות מהגלריה, הזמנות והגדרות. הקישור אישי ותקף לזמן מוגבל; אל תעבירו אותו לאחרים.</p>
                </section>
              )}

              {/* Managed full-stack app: the generated admin panel (users, data, automations,
                  integrations, backups), opened with the owner's own short-lived session. */}
              {m.previewUrl && m.adminUrl && (
                <section className="chat-build-explanation" aria-label="ניהול האפליקציה">
                  <span className="chat-build-explanation-label">ניהול האפליקציה</span>
                  <p><a href={m.adminUrl} target="_blank" rel="noopener noreferrer">פתיחת עמוד הניהול</a> - נתונים, משתמשים ותפקידים, אוטומציות, חיבור שירותים (מייל, SMS, סליקה, AI) וגיבויים. הקישור אישי ותקף ל-8 שעות; אל תעבירו אותו לאחרים.</p>
                </section>
              )}

              {m.previewUrl && i === lastPreviewIndex && previewVisible && (
                <PreviewFrame
                  loading={false}
                  previewUrl={m.previewUrl}
                  pages={m.previewPages}
                  expiresAt={m.previewExpiresAt}
                  target={m.target || 'WEB'}
                  targetPlatforms={m.targetPlatforms || []}
                  title="תצוגת האתר שנבנה"
                  returnLabel="חזרה לצ׳אט"
                  onClose={() => setPreviewVisible(false)}
                  onRefreshPreview={m.sandboxJobId ? () => refreshPreview(m.previewUrl, m.sandboxJobId) : undefined}
                />
              )}
              {/* The Android / iPhone app the customer asked for: its price and approve button right
                  under the finished Preview (live 2026-11-04: the iPhone step was never found). */}
              {m.previewUrl && i === lastPreviewIndex && m.projectId && requestedNativePanel(m.target, m.targetPlatforms) && (
                <section className="chat-next-steps chat-native-offer" dir="rtl" aria-label="בניית האפליקציה שביקשת">
                  <strong className="chat-next-steps-title">{nativeOfferTitle(requestedNativePlatforms(m.target, m.targetPlatforms))}</strong>
                  {requestedNativePlatforms(m.target, m.targetPlatforms).includes('IOS') && <IosAppPanel projectId={m.projectId} />}
                  {requestedNativePlatforms(m.target, m.targetPlatforms).includes('ANDROID') && <AndroidPackagePanel projectId={m.projectId} target={m.target || 'MOBILE'} targetPlatforms={m.targetPlatforms || []} />}
                </section>
              )}
              {m.previewUrl && i === lastPreviewIndex && m.projectId && (
                <MyAppButton
                  projectId={m.projectId}
                  openSignal={myAppOpenSignal}
                  disabled={sending}
                  onChat={text => handleSend(null, { task: text })}
                />
              )}
              {/* Older messages without a saved project keep the previous card. */}
              {m.previewUrl && i === lastPreviewIndex && !m.projectId && (
                <ChatNextSteps
                  projectId={m.projectId || null}
                  previewUrl={m.previewUrl}
                  target={m.target || 'WEB'}
                  targetPlatforms={m.targetPlatforms || []}
                  disabled={sending}
                  onPublish={() => handleSend(null, { task: PUBLISH_REQUEST_TEXT })}
                />
              )}
              {m.previewUrl && i === lastPreviewIndex && !previewVisible && (
                <button type="button" className="btn preview-reopen" onClick={() => setPreviewVisible(true)}>פתח שוב את התצוגה</button>
              )}
            </div>
          ));
        })()}
        {sending && (
          <div className="chat-bubble agent">
            <BuildProgressTimeline
              entries={buildActivity?.log?.length ? buildActivity.log : liveProgressLog}
              currentMessage={describeAgentProgress(buildActivity, liveProgress, activeTaskKind)}
            />
            {activeTaskVisualEvidence.length > 0 && (
              <div className="chat-file-note">תמונה מצורפת למשימה הפעילה: {activeTaskVisualEvidence.map(evidence => evidence.fileName).join(', ')}</div>
            )}
          </div>
        )}
        {pendingVisualSend && (
          <section className="visual-evidence-review" aria-label="אישור פרטים מתמונה">
            <div className="visual-evidence-review-heading">
              <div>
                <span className="chat-build-explanation-label">בדיקת פרטים מהתמונה</span>
                <h3>אשרו או תקנו לפני שהסוכן משתמש בפרטים</h3>
              </div>
              <button type="button" className="btn" onClick={() => setExpandedImage(pendingVisualSend.attachment)}>הגדל תמונה</button>
            </div>
            <p>הסוכן יקבל רק את הפרטים שאישרתם. קובץ המקור יישמר באופן פרטי רק עבור הבנייה שאושרה, ולא ייכנס להיסטוריית הצ׳אט.</p>
            <div className="visual-evidence-fields">
              <label>שם העסק<input value={pendingVisualSend.facts.businessName || ''} onChange={event => updatePendingVisualFact('businessName', event.target.value)} maxLength="160" /></label>
              <label>טקסט בלוגו<input value={pendingVisualSend.facts.logoText || ''} onChange={event => updatePendingVisualFact('logoText', event.target.value)} maxLength="160" /></label>
              <label>מספרי טלפון, מופרדים בפסיק<input value={(pendingVisualSend.facts.phoneNumbers || []).join(', ')} onChange={event => updatePendingVisualFact('phoneNumbers', event.target.value, true)} maxLength="220" /></label>
              <label>כתובת<input value={pendingVisualSend.facts.address || ''} onChange={event => updatePendingVisualFact('address', event.target.value)} maxLength="240" /></label>
              <label>שעות פעילות<input value={pendingVisualSend.facts.serviceHours || ''} onChange={event => updatePendingVisualFact('serviceHours', event.target.value)} maxLength="120" /></label>
              <label>שירותים, מופרדים בפסיק<input value={(pendingVisualSend.facts.services || []).join(', ')} onChange={event => updatePendingVisualFact('services', event.target.value, true)} maxLength="400" /></label>
            </div>
            <div className="visual-evidence-actions">
              <button type="button" className="submit-btn" onClick={confirmVisualAndSend} disabled={sending}>אישור והמשך לשיחה</button>
              <button type="button" className="btn" onClick={() => setPendingVisualSend(null)} disabled={sending}>ביטול</button>
            </div>
          </section>
        )}
        <div ref={bottomRef} />
      </div>

      {error && (
        <div className="auth-error">
          {error}
          {needsUpgrade && onNavigateToAccount && (
            <>
              {' '}
              <button type="button" className="submit-btn" onClick={onNavigateToAccount}>Upgrade now</button>
            </>
          )}
        </div>
      )}


      <form onSubmit={handleSend} className="chat-input-row">
        <input ref={fileInputRef} type="file" accept="image/*,.zip,.txt,.md,.csv,.json,.xml,application/zip,application/x-zip-compressed,text/plain,text/markdown,text/csv,application/json,application/xml" hidden onChange={handleAttachment} />
        <button type="button" className="chat-input-action gallery-action" title="צירוף תמונה, קובץ או ZIP" aria-label="צירוף תמונה, קובץ או ZIP" onClick={() => fileInputRef.current?.click()} disabled={sending || Boolean(pendingVisualSend)}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="m4 18 5-5 3.5 3 2.5-2.5 5 4.5" /></svg>
        </button>
        <button type="button" className={`chat-input-action mic-action${listening ? ' is-listening' : ''}`} title={listening ? 'סיום הקלטה' : 'הכתבה קולית'} aria-label={listening ? 'סיום הקלטה' : 'הכתבה קולית'} onClick={toggleMic} disabled={sending || Boolean(pendingVisualSend)} aria-pressed={listening}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M6 11a6 6 0 0 0 12 0M12 17v4M8 21h8" /></svg>
        </button>
        {/* One grid cell for the extra composer tools (more menu + project type). Without it the
            composer grid (public/chat-composer.css) auto-placed both into 44px cells and the
            chip overflowed sideways on phones (live report 2026-10-24). */}
        <div className="chat-input-tools">
          <div className="chat-more-menu">
            <button type="button" className="chat-input-action" aria-label="אפשרויות נוספות" title="אפשרויות נוספות" aria-haspopup="menu" aria-expanded={moreMenuOpen}
              onClick={() => setMoreMenuOpen(current => !current)} disabled={sending || Boolean(pendingVisualSend)}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="5" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="12" cy="19" r="1.4" /></svg>
            </button>
            {moreMenuOpen && (
              <div className="chat-more-menu-list" role="menu">
                <button type="button" role="menuitem" onClick={() => { setMoreMenuOpen(false); setTargetPickerOpen(true); }}>סוג הפרויקט…</button>
                <button type="button" role="menuitem" onClick={() => { setMoreMenuOpen(false); fileInputRef.current?.click(); }}>צירוף תמונה או קובץ</button>
              </div>
            )}
          </div>
          <TargetPicker value={targetChoice} open={targetPickerOpen} onToggle={setTargetPickerOpen} onChange={setTargetChoice} disabled={sending || Boolean(pendingVisualSend)} />
        </div>
        <textarea
          rows="1"
          value={input}
          onPaste={handlePaste}
          onChange={e => {
            setInput(e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 124)}px`;
          }}
          onFocus={e => setTimeout(() => e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80)}
          placeholder={isWelcome ? t('chat.welcomePlaceholder') : 'Ask the agent to do something'}
          disabled={sending || Boolean(pendingVisualSend)}
        />
        <button type="submit" className="submit-btn" disabled={sending || listening || Boolean(pendingVisualSend) || (!input.trim() && !attachment)}>Send</button>
        {attachment && <div className="chat-attachment-preview">
          {attachment.kind === 'image' ? <img src={attachment.url} alt={attachment.name} /> : <span className="chat-file-badge" aria-hidden="true">{attachment.kind === 'zip' ? 'ZIP' : 'TXT'}</span>}
          <span>{attachment.name}</span>
          {attachment.kind === 'zip' && <small>{Math.ceil(attachment.size / 1024 / 1024)}MB · עד 50MB דחוס / 500MB לאחר חילוץ</small>}
          {attachment.kind === 'image' && <a href={attachment.url} download={attachment.name}>הורדה</a>}
          <button type="button" onClick={() => { attachmentSelectionRef.current += 1; releaseImageObjectUrl(attachment.url); setAttachment(null); setAttachmentNotice(''); }}>הסרה</button>
        </div>}
      </form>
      {listening && <div className="chat-recording-status" role="status">מקליט… לחצו שוב על המיקרופון לסיום ההכתבה</div>}
      {attachmentNotice && <p className="chat-attachment-notice" role="status">{attachmentNotice}</p>}
      {expandedImage?.url && (
        <div className="chat-image-lightbox" role="dialog" aria-modal="true" aria-label={t('chat.expandedImage')}>
          <button type="button" className="chat-image-lightbox-backdrop" aria-label={t('chat.closeImage')} onClick={() => setExpandedImage(null)} />
          <section className="chat-image-lightbox-content">
            <div className="chat-image-lightbox-actions">
              <span>{expandedImage.name || t('chat.imageAttachment')}</span>
              <div>
                <a href={expandedImage.url} download={expandedImage.name || 'codevoyage-image'}>{t('chat.downloadImage')}</a>
                <button type="button" onClick={() => setExpandedImage(null)}>{t('chat.closeImage')}</button>
              </div>
            </div>
            <img src={expandedImage.url} alt={expandedImage.name || t('chat.imageAttachment')} />
          </section>
        </div>
      )}
      </div>
    </>
  );
}

export default AgentChat;
